import React, { useState, useEffect } from 'react';
import { X, MessageCircle, Search, RefreshCw } from 'lucide-react';
import PresenceDot from './PresenceDot';

const WhisperConversations = ({ isOpen, onClose, onSelectConversation }) => {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFriendsList, setShowFriendsList] = useState(false);
  const [friends, setFriends] = useState([]);
  const [deletingId, setDeletingId] = useState(null);

  const API = process.env.REACT_APP_BACKEND_URL + '/api';

  // Load conversations
  const loadConversations = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('beatseek_token');
      const response = await fetch(`${API}/whispers/conversations`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setConversations(data.conversations || []);
      }
    } catch (error) {
      console.error('Error loading conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  // Load friends list
  const loadFriends = async () => {
    try {
      const token = localStorage.getItem('beatseek_token');
      const response = await fetch(`${API}/friends`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setFriends(data.friends || []);
      }
    } catch (error) {
      console.error('Error loading friends:', error);
    }
  };

  // Delete conversation (soft-delete for current user only)
  const deleteConversation = async (otherUserId, otherUserName, e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    if (!window.confirm(`Delete conversation with ${otherUserName}? This only removes it from your view.`)) return;
    setDeletingId(otherUserId);
    try {
      const token = localStorage.getItem('beatseek_token');
      const response = await fetch(`${API}/whispers/conversation/${otherUserId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (response.ok) {
        setConversations(prev => prev.filter(c => c.user_id !== otherUserId));
      } else {
        alert('Failed to delete conversation');
      }
    } catch (error) {
      console.error('Error deleting conversation:', error);
      alert('Failed to delete conversation');
    } finally {
      setDeletingId(null);
    }
  };

  useEffect(() => {
    if (isOpen) {
      loadConversations();
      loadFriends();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const filteredConversations = conversations.filter(conv =>
    conv.user_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-[9999]">
      <div className="bg-gradient-to-br from-gray-900 via-purple-900/50 to-gray-900 rounded-2xl border border-purple-500/30 w-full max-w-md max-h-[600px] flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-700/50">
          <div className="flex items-center gap-2">
            <MessageCircle size={24} className="text-purple-400" />
            <h3 className="text-white font-semibold text-lg">Whispers</h3>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowFriendsList(!showFriendsList)}
              className="text-sm bg-purple-600 hover:bg-purple-700 text-white px-3 py-1.5 rounded-lg transition-colors font-medium"
              data-testid="whisper-new-conv-btn"
            >
              + New
            </button>
            <button
              onClick={loadConversations}
              disabled={loading}
              className="text-gray-400 hover:text-white transition-colors p-2 hover:bg-gray-800/50 rounded-lg disabled:opacity-50"
              title="Refresh conversations"
              data-testid="whisper-refresh-btn"
            >
              <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
            </button>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white transition-colors p-2 hover:bg-gray-800/50 rounded-lg"
              data-testid="whisper-close-btn"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="p-4 border-b border-gray-700/50">
          <div className="relative">
            <Search size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search conversations..."
              className="w-full bg-gray-800/50 border border-gray-700/50 rounded-lg pl-10 pr-4 py-2 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>
        </div>

        {/* Conversations List or Friends List */}
        <div className="flex-1 overflow-y-auto">
          {showFriendsList ? (
            // Friends List
            <div className="p-2">
              <div className="flex items-center justify-between p-3 border-b border-gray-700/50">
                <h4 className="text-white font-semibold text-sm">Select a Friend</h4>
                <button
                  onClick={() => setShowFriendsList(false)}
                  className="text-xs text-gray-400 hover:text-white"
                >
                  Back
                </button>
              </div>
              {friends.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center p-8">
                  <MessageCircle size={48} className="text-gray-600 mb-4" />
                  <h4 className="text-lg font-semibold text-white mb-2">No friends yet</h4>
                  <p className="text-gray-400 text-sm">Add friends to start whispering!</p>
                </div>
              ) : (
                friends.map((friend) => {
                  // Construct proper avatar URL
                  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
                  let avatarUrl = null;
                  if (friend.friend_avatar) {
                    avatarUrl = friend.friend_avatar.startsWith('/uploads/') 
                      ? `${BACKEND_URL}/api${friend.friend_avatar}`
                      : friend.friend_avatar.startsWith('/api/') 
                      ? `${BACKEND_URL}${friend.friend_avatar}`
                      : friend.friend_avatar;
                  }
                  
                  return (
                    <button
                      key={friend.friend_id}
                      onClick={() => {
                        onSelectConversation({
                          user_id: friend.friend_id,
                          user_name: friend.friend_name,
                          user_avatar: avatarUrl,
                          online: false
                        });
                        setShowFriendsList(false);
                        onClose();
                      }}
                      className="w-full flex items-center gap-3 p-3 hover:bg-gray-800/50 rounded-lg transition-all duration-200 text-left"
                    >
                      <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-blue-500 rounded-full flex items-center justify-center text-white font-semibold overflow-hidden">
                        {avatarUrl ? (
                          <img src={avatarUrl} alt={friend.friend_name} className="w-full h-full object-cover" />
                        ) : (
                          friend.friend_name?.charAt(0).toUpperCase() || 'U'
                        )}
                      </div>
                      <div className="flex-1">
                        <h4 className="text-white font-semibold text-sm">{friend.friend_name}</h4>
                        <p className="text-xs text-gray-400">Click to start whispering</p>
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          ) : loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
            </div>
          ) : filteredConversations.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center p-8">
              <MessageCircle size={48} className="text-gray-600 mb-4" />
              <h4 className="text-lg font-semibold text-white mb-2">No conversations yet</h4>
              <p className="text-gray-400 text-sm">
                {searchTerm ? 'No conversations match your search' : 'Start whispering with your mutual friends!'}
              </p>
            </div>
          ) : (
            <div className="p-2">
              {filteredConversations.map((conv) => (
                <div
                  key={conv.user_id}
                  className="relative group"
                >
                  <button
                    onClick={() => {
                      onSelectConversation(conv);
                      onClose();
                    }}
                    className="w-full flex items-center gap-3 p-3 pr-10 hover:bg-gray-800/50 rounded-lg transition-all duration-200 text-left"
                    data-testid={`whisper-conv-${conv.user_id}`}
                  >
                    <div className="relative">
                      <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-blue-500 rounded-full flex items-center justify-center text-white font-semibold overflow-hidden">
                        {conv.user_avatar ? (
                          <img src={conv.user_avatar} alt={conv.user_name} className="w-full h-full object-cover" />
                        ) : (
                          conv.user_name?.charAt(0).toUpperCase() || 'U'
                        )}
                      </div>
                      <div className="absolute -bottom-0.5 -right-0.5">
                        <PresenceDot online={false} size="small" />
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <h4 className="text-white font-semibold text-sm truncate">{conv.user_name}</h4>
                        {conv.unread_count > 0 && (
                          <span className="bg-purple-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                            {conv.unread_count}
                          </span>
                        )}
                      </div>
                      <p className="text-gray-400 text-xs truncate">{conv.last_message}</p>
                      <p className="text-gray-500 text-xs mt-1">
                        {new Date(conv.last_message_time).toLocaleString([], { 
                          month: 'short', 
                          day: 'numeric', 
                          hour: '2-digit', 
                          minute: '2-digit' 
                        })}
                      </p>
                    </div>
                  </button>
                  <button
                    onClick={(e) => deleteConversation(conv.user_id, conv.user_name, e)}
                    disabled={deletingId === conv.user_id}
                    className="absolute top-2 right-2 p-1.5 text-gray-500 hover:text-red-400 hover:bg-red-500/10 rounded-md transition-all opacity-70 hover:opacity-100 disabled:opacity-30"
                    title="Delete conversation"
                    data-testid={`whisper-delete-conv-${conv.user_id}`}
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WhisperConversations;
